/**
 * Manufacturer catalog-code and barcode conventions.
 *
 * Used by review primers (`brandIdentifierPolicy`) and as the playbook when
 * adding a scraper. Confidence:
 * - motzu — observed in our scraper / tests
 * - researched — official datasheet or retailer listing (B&H, EU distributor)
 * - inferred — GS1 country + industry pattern; do not treat as a hard rule
 *
 * `barcodeModel` is a live rule: ean-as-upc and independent-regional drive
 * deriveIdentifiers, review notes, and AI prompts. `unknown` stays default GS1.
 *
 * Keep in sync with src/utils/brandCatalogIdentity.ts.
 */

export type IdentityConfidence = 'motzu' | 'researched' | 'inferred';

/** How Motzu should treat the retail barcode once we scrape or ingest the brand. */
export type BarcodeModel =
  | 'upc-a'
  | 'ean-as-upc'
  | 'independent-regional'
  | 'unknown';

export interface BrandCatalogIdentity {
  brandId: string;
  aliases?: string[];
  name: string;
  /** Scheduled manufacturer scraper exists. */
  carried: boolean;
  confidence: IdentityConfidence;
  skuLooksLike: string;
  skuExample?: string;
  mpnRule: string;
  barcodeModel: BarcodeModel;
  upcLooksLike: string;
  eanLooksLike?: string;
  gs1Notes?: string;
  scraperHint?: string;
  source: string;
}

const DEFAULT_UPC =
  'US/Canada barcode, usually 12 digits. A leading zero is normal for many US UPC-A codes.';
const DEFAULT_EAN =
  '13-digit international barcode. Often 0 + the UPC. That leading zero is padding, not a different product.';
const EAN_AS_UPC_TEXT =
  'This brand typically publishes EAN-13 only (often GS1 CN 69…). A 13-digit value in UPC is the retail barcode, not a padded UPC-A.';
const INDEPENDENT_EAN_TEXT =
  'Regional EAN/JAN can be a different GS1 number than the US UPC. Do not invent EAN by zero-padding the UPC.';

export const BRAND_CATALOG_IDENTITY: BrandCatalogIdentity[] = [
  // --- Motzu-carried, verified ---
  {
    brandId: 'canon',
    name: 'Canon',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Canon: retail item code like 1483C002. Kit vs body codes (1483C010 vs 1483C002) are different simples.',
    skuExample: '1483C002',
    mpnRule: 'Usually the same item code. Kit vs body stay separate simples.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'PDP item code + pricing tests',
  },
  {
    brandId: 'nikon',
    name: 'Nikon',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Nikon: numeric storefront id (e.g. 20089), not a NIKKOR URL slug.',
    skuExample: '20089',
    mpnRule: 'Same numeric catalog id. Reject NIKKORZ… slugs.',
    barcodeModel: 'independent-regional',
    upcLooksLike: 'Nikon US UPC-A is 12 digits (often 018208…). Do not replace it with the Japan JAN.',
    eanLooksLike: 'Nikon may have a Japan JAN (45… / 49…) that is a different number from the US UPC. That is normal.',
    gs1Notes: 'US GS1 018208 vs Japan JAN 45/49',
    source: 'nikonusa URL /p/{slug}/{id} + identifier tests',
  },
  {
    brandId: 'sony',
    name: 'Sony',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Sony: body/lens code with color suffix, e.g. ILCE7M4-B or SEL2470GM2.',
    skuExample: 'ILCE7M4-B',
    mpnRule: 'Enrichment may add slash form ILCE-7M5/B — same simple, not a second SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'JSON-LD sku; test UPC 027242920002',
  },
  {
    brandId: 'fujifilm',
    name: 'Fujifilm',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Fujifilm: numeric catalog code, at least 8 digits (e.g. 16756924). SKU and MPN must match.',
    skuExample: '16756924',
    mpnRule: 'Must equal SKU. Never a lens string like XF50mmF2.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'Shopusa hyphen UPC (0-xxxxx-xxxxx-x) is a 12-digit UPC-A. Strip hyphens; keep the leading zero.',
    source: 'shopusa JSON-LD + fujifilmProductCodes',
  },
  {
    brandId: 'panasonic',
    name: 'Panasonic',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Panasonic: Shopify variant.sku — often DC-S5M2 / DC-S5M2K (kit suffix) or a V… internal code. Not the URL handle.',
    skuExample: 'DC-S5M2',
    mpnRule: 'Same as variant.sku.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    scraperHint: 'Non-camera Panasonic accessories. variant.barcode → upc. Lumix S/GH bodies use brandId lumix.',
    source: 'shop.panasonic.com accessories',
  },
  {
    brandId: 'lumix',
    aliases: ['panasonic lumix'],
    name: 'Lumix',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Lumix: Shopify variant.sku — often DC-S5M2 / DC-S5M2K (kit suffix) or a V… internal code. Not the URL handle.',
    skuExample: 'DC-S5M2',
    mpnRule: 'Same as variant.sku.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    scraperHint: 'shop.panasonic.com Lumix cameras/lenses. variant.barcode → upc.',
    source: 'shop.panasonic.com / Lumix Shopify',
  },
  {
    brandId: 'omsystem',
    aliases: ['om-system', 'om system'],
    name: 'OM System',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'OM System: JSON-LD mpn. Strip store-config suffixes like -U4 from sku.',
    skuExample: 'V2100200U000',
    mpnRule: 'JSON-LD mpn is the catalog identity.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'JSON-LD gtin12 only — 12-digit UPC-A. Do not invent EAN if gtin13 is absent.',
    scraperHint: 'Registry id is om-system; scraper brandId is omsystem.',
    source: 'explore.omsystem.com JSON-LD',
  },
  {
    brandId: 'ricoh-imaging',
    aliases: ['pentax', 'ricoh', 'ricoh imaging'],
    name: 'Ricoh Imaging',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Ricoh/Pentax: numeric Woo variation sku (21410). Skip -RM/-OB/-SM, parent aggregates like 21410-21420, and store-cart bundles (KB15994G, THETASC2B, GR3PB, *KIT*).',
    skuExample: '21410',
    mpnRule: 'Same as the numeric item code. Do not copy KB* cart codes to MPN.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'us.ricoh-imaging.com Woo variations',
  },
  {
    brandId: 'sigma',
    name: 'Sigma',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Sigma: US itemprop sku; CA Shopify sku. Model-like A057 / 150600 on some lines.',
    skuExample: 'A057',
    mpnRule: 'Same as SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'CA Shopify: 12→upc, 13→ean. US site often has no barcode.',
    source: 'sigmaphoto.com + CA Shopify',
  },
  {
    brandId: 'tamron',
    name: 'Tamron',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Tamron: Woo variation sku, model-like A047 / A058 per mount.',
    skuExample: 'A047',
    mpnRule: 'Same as variation sku.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'tamron-usa.com Woo variations',
  },
  {
    brandId: 'smallrig',
    name: 'SmallRig',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'SmallRig: API thirdpartySkuCode. Bundles starting B0… are not the barcode keeper.',
    skuExample: '5783',
    mpnRule: 'Same as thirdpartySkuCode.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: EAN_AS_UPC_TEXT,
    gs1Notes: 'GS1 CN 694… — Motzu stores EAN-13 in upc.',
    source: 'SmallRig API skuSpecs; test 6941590025537',
  },
  {
    brandId: 'viltrox',
    name: 'Viltrox',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Viltrox: manufacturer JYC code, e.g. JYC-03262 or JYC-03489-PL.',
    skuExample: 'JYC-03262',
    mpnRule: 'Same JYC code (normalize case).',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: EAN_AS_UPC_TEXT,
    gs1Notes: 'GS1 CN 695… — Motzu stores EAN-13 in upc.',
    source: 'Shopify + viltroxProductCodes; test 6953400308785',
  },
  {
    brandId: 'rokinon',
    name: 'Rokinon',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Rokinon: per-mount model, e.g. DSX35-RF, DSX35-C, DSX35-NEX.',
    skuExample: 'DSX35-RF',
    mpnRule: 'Same mount-specific model.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'Accordion / metafield UPC like 0-84438-76700-1 is UPC-A. Strip hyphens; keep the leading zero.',
    source: 'buyrokinon metafields + Shopify',
  },
  {
    brandId: 'nisi',
    name: 'NiSi',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'NiSi global SKU: N-* or short numeric (e.g. N-156361). Not the USA regional NIM-/NIP-* code.',
    skuExample: 'N-156361',
    mpnRule: 'USA regional SKU NIM-* (or older NIP-*) from the PDP / Promaster warehouse.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike:
      'PDP Barcode is often EAN-13 (697…). Scraper maps 12→upc, 13→ean; writer stores EAN-13 in upc.',
    gs1Notes: 'Example 6972949373597 (GS1 CN). Motzu stores EAN-13 in upc.',
    scraperHint: 'USA NIM-/NIP-* + barcode only on full PDP scrape, not price_only.',
    source: 'nisiopticsusa.com Woo + PDP',
  },
  {
    brandId: 'rockynook',
    aliases: ['rocky nook', 'rocky-nook', 'rocky_nook'],
    name: 'Rocky Nook',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Rocky Nook: the publisher item number, a short integer (e.g. 1386), which is also the cover image file prefix. Not the ISBN and not the URL slug.',
    skuExample: '1386',
    mpnRule: 'Same item number.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike:
      'Books carry an ISBN-13 (Bookland EAN, 978… or 979…) as the only retail barcode. Motzu stores it in upc and ean; there is no 12-digit UPC-A.',
    eanLooksLike: 'The ISBN-13 printed on the back cover, e.g. 9798888143421. An ISBN-10 is not a barcode.',
    gs1Notes: 'GS1 Bookland prefix 978/979. The ebook edition has its own ISBN; Motzu stages the print edition only.',
    scraperHint: 'Print-edition price and ISBN from the WooCommerce Store API; author, subtitle and the attribute table from the product page on a full run.',
    source: 'rockynook.com Woo Store API + PDP',
  },
  {
    brandId: 'hasselblad',
    name: 'Hasselblad',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Hasselblad: CP.HB.00000nnn.01 style part number (same family as DJI CP.MA…).',
    skuExample: 'CP.HB.00000927.01',
    mpnRule: 'Same CP.HB code. Retailers also list a short reference (2X0002) — prefer CP.HB.',
    barcodeModel: 'unknown',
    upcLooksLike:
      'EU listings use EAN-13 739… (Sweden). Shopify barcode is often empty — do not invent 0+UPC.',
    eanLooksLike: 'Example X2D II: EAN 7392544119250 next to CP.HB.00000925.01.',
    gs1Notes: 'GS1 SE 739. Dual barcode vs UPC-A not Motzu-verified.',
    source: 'store-na.hasselblad.com products.json + EU retailer EAN',
  },
  {
    brandId: 'westcott',
    name: 'Westcott',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Westcott: Shopify variant.sku (often a short numeric catalog).',
    mpnRule: 'Vendor SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'JSON-LD / metafield Product UPC plus Shopify barcode — 12-digit UPC-A.',
    source: 'fjwestcott.com',
  },
  {
    brandId: 'benro',
    name: 'Benro',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Benro: API catalog sku / productCode. URL handle only if skuSource=handle.',
    mpnRule: 'Scraper often leaves MPN empty — fill from catalog sku.',
    barcodeModel: 'unknown',
    upcLooksLike: DEFAULT_UPC,
    source: 'Benro catalog API',
  },
  {
    brandId: 'promaster',
    aliases: ['Promaster', 'Pro Master'],
    name: 'ProMaster',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'ProMaster: Shopify variant.sku. Warehouse manusku can differ — that is MPN, not a second simple.',
    mpnRule: 'Warehouse feed uses manusku as MPN.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'ProMaster dealer API + warehouse',
  },
  {
    brandId: 'aputure',
    name: 'Aputure',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Aputure: standalone fixture/cable codes (AP30332A25, AA0506020W). Hyphenated codes (AP30332A25-APA0172A30) are promotional bundles, not the base simple.',
    skuExample: 'AP30332A25',
    mpnRule:
      'Dealer/sheet MPN is the standalone simple. Do not replace it with the current aputure.com promo SKU (base-accessory). Keep a distinct bundle GTIN when the site is selling a free-accessory kit.',
    barcodeModel: 'unknown',
    upcLooksLike:
      'US standalone is often GTIN-12 (e.g. 850065294339). Aputure.com may label a GTIN-13 as UPC on a bundle. Shopify barcode is often empty. Do not copy the sheet UPC onto a hyphenated bundle SKU, and do not invent a UPC from the SKU.',
    source: 'Amgreat/Aputure dealer book + aputure.com + B&H (base vs promo bundle)',
  },
  {
    brandId: 'deity',
    aliases: ['deity microphones'],
    name: 'Deity',
    carried: false,
    confidence: 'researched',
    skuLooksLike:
      'Deity: alphanumeric factory code, e.g. DTS0253D63 or DP04000475. Skip sheet section headers (LAVALIER, MICROPHONE).',
    skuExample: 'DTS0253D63',
    mpnRule: 'Same factory SKU. US vs Global kits (DTR0297D71 vs DTR0297D72) are different simples.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: EAN_AS_UPC_TEXT,
    gs1Notes: 'Dealer book UPCs are GS1 CN 697… EAN-13. Do not invent a 12-digit UPC by dropping a digit.',
    source: 'Amgreat / Deity dealer price book + deitymic.com',
  },
  {
    brandId: 'peak_design',
    aliases: ['peak design'],
    name: 'Peak Design',
    carried: true,
    confidence: 'researched',
    skuLooksLike: 'Peak Design: product+color code, e.g. CLC-N-1, L-MN-3, M-WA-AB-MN-1.',
    skuExample: 'CLC-N-1',
    mpnRule: 'Same manufacturer part.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'US UPC-A (855110… / 818373…). EAN is usually 0 + UPC.',
    source: 'Retailer listings; Shopify sku on peakdesign.com',
  },
  {
    brandId: 'wandrd',
    name: 'WANDRD',
    carried: true,
    confidence: 'researched',
    skuLooksLike:
      'WANDRD: {line}{size}-{color}[-{bundle}]-{version}, e.g. PK31-BK-4, PK31-BK-PB-4 (Photography Bundle). Accessory parts use an XP- prefix.',
    skuExample: 'PK31-BK-4',
    mpnRule: 'Same code as SKU. The schema.org mpn on wandrd.com is the Shopify product id, not a part number.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'US UPC-A across several GS1 prefixes (850063…, 850054…, 851459…). EAN is usually 0 + UPC.',
    source: 'Live wandrd.com Shopify catalog (variant sku + barcode from /products/<handle>.json), 2026-10-02',
  },
  {
    brandId: 'polaroid',
    name: 'Polaroid',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Polaroid: 5–6 digit catalog, e.g. 009211 (camera) or 006663 (film).',
    skuExample: '009211',
    mpnRule: 'Same numeric catalog.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'shop-us.polaroid.com products.json',
  },
  {
    brandId: 'think_tank',
    aliases: ['think tank', 'think tank photo'],
    name: 'Think Tank Photo',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Think Tank: Shopify variant.sku (often a short numeric bag code).',
    mpnRule: 'Vendor SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'thinktankphoto.com',
  },
  {
    brandId: 'tiffen',
    name: 'Tiffen',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Tiffen: Shopify variant.sku — often size+filter (77UVP) or a numeric catalog.',
    mpnRule: 'Vendor SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'tiffen.com',
  },
  {
    brandId: 'smallhd',
    name: 'SmallHD',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'SmallHD: Shopify variant.sku (monitor / accessory part number).',
    mpnRule: 'Vendor SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'smallhd.com',
  },
  {
    brandId: 'pelican',
    name: 'Pelican',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Pelican: Shopify variant.sku — case model (1510) plus color/foam variant.',
    mpnRule: 'Vendor SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'shop.pelican.com',
  },
  {
    brandId: 'nanuk',
    name: 'Nanuk',
    carried: true,
    confidence: 'researched',
    skuLooksLike:
      'NANUK: {model}S-{interior}{color}-{rev}[-{insert}], e.g. 935S-000BK-0A0 (935, empty, black). Interior 000 empty, 010 cubed foam, 020 padded divider, 060 Pro Photo Kit, 080 a fitted insert for one device, which adds an insert suffix (915S-080BK-0A0-C2423). Bags (N30L-000BK-0B1) and parts (S903-K201, 40-93306-K) follow their own codes.',
    skuExample: '935S-000BK-0A0',
    mpnRule: 'Same code as SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: "NANUK's own UPC-A prefix 666365…. EAN is 0 + UPC.",
    source: 'Live nanuk.com Shopify catalog (variant sku + barcode from /products/<handle>.json), 2026-10-09',
  },
  {
    brandId: 'tokina',
    name: 'Tokina',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Tokina: Shopify variant.sku (US distributor). Prefer the lens model, not the handle.',
    mpnRule: 'Vendor SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'tokinausa.com',
  },
  {
    brandId: 'meike',
    name: 'Meike',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Meike: Shopify sku is often blank — do not keep a handle. Prefer the model+mount printed on the PDP.',
    mpnRule: 'Manufacturer model+mount when sku is empty.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike:
      'China house; barcodes are typically EAN-13 69…. Shopify barcode is often empty. Store a scraped EAN-13 in upc.',
    gs1Notes: 'GS1 CN 69… — Motzu stores EAN-13 in upc when a barcode is present.',
    scraperHint: 'Live products.json (2026-09) had sku=null and barcode=null on current lenses.',
    source: 'meikeglobal.com products.json',
  },
  {
    brandId: 'cinestill',
    name: 'CineStill',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'CineStill: Shopify variant.sku (stock + format, e.g. 800T-36exp, 400D-120, Cs41-quart).',
    skuExample: '800T-36exp',
    mpnRule: 'Vendor SKU.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: EAN_AS_UPC_TEXT,
    gs1Notes: 'GS1 DE 4260243… on every CineStill box, US stock included — Motzu stores EAN-13 in upc.',
    source: 'cinestillfilm.com /products/{handle}.json variant barcode; 800T-36exp = 4260243553596',
  },
  {
    brandId: 'vivitar',
    name: 'Vivitar',
    carried: true,
    confidence: 'motzu',
    skuLooksLike: 'Vivitar: color-suffixed camera SKUs, e.g. DVR783-BLK / DVR783-SIL.',
    skuExample: 'DVR783-BLK',
    mpnRule: 'Same color SKU.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'Shopify barcode is often empty.',
    scraperHint: 'Photo/video collections only — exclude lifestyle SKUs.',
    source: 'vivitar.com collections',
  },
  {
    brandId: 'phottix',
    aliases: ['Photoolex'],
    name: 'Phottix',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Phottix: 5-digit PRO Distributors catalog code on phottixus.com (e.g. 86192). Warehouse manusku is PH* and is the MPN, not the simple.',
    skuExample: '86192',
    mpnRule: 'Manufacturer PH* code from Promaster warehouse (PH85380). Do not treat the 5-digit Shopify SKU as Promaster.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike:
      'Warehouse EAN-13 48946… (GS1 HK). Shopify barcode is empty or dummy "0". Do not invent a UPC from the 5-digit SKU.',
    scraperHint:
      'Match warehouse by decoded product title — Shopify SKU does not equal manusku. Vendor field is Promaster.',
    source: 'phottixus.com products.json + promaster warehouse',
  },

  // --- Researched, not yet scraped ---
  {
    brandId: 'leica',
    name: 'Leica',
    carried: false,
    confidence: 'researched',
    skuLooksLike:
      'Leica: 5-digit order number, often written with a space (20 200). Same body can have regional order nos (EU/US vs JP vs ROW).',
    skuExample: '20200',
    mpnRule: 'Order number is the identity. Type no. (2416) is the platform, not the SKU.',
    barcodeModel: 'independent-regional',
    upcLooksLike:
      'Do not assume one UPC worldwide. JP/ROW order numbers are different simples — barcodes likely differ too.',
    eanLooksLike: INDEPENDENT_EAN_TEXT,
    source: 'Leica M11 / M11-P technical data (order no. by region)',
  },
  {
    brandId: 'phase_one',
    aliases: ['phase one'],
    name: 'Phase One',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Phase One: numeric packaging / accessory P/N (e.g. 73198) plus marketing name (IQ4 150MP).',
    skuExample: '73198',
    mpnRule: 'Prefer the numeric P/N over the marketing name.',
    barcodeModel: 'unknown',
    upcLooksLike: DEFAULT_UPC,
    source: 'Phase One XF IQ4 accessory brochure',
  },
  {
    brandId: 'gopro',
    name: 'GoPro',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'GoPro: kit MPN CHDHX-nnn-RW (HERO body). Retailer SKUs (W128…) are not the manufacturer code.',
    skuExample: 'CHDHX-131-RW',
    mpnRule: 'CHD… / AJ… accessory codes. Do not re-SKU (GoPro reseller policy).',
    barcodeModel: 'upc-a',
    upcLooksLike: 'US UPC-A (example HERO13 810116381616). EAN is usually 0 + UPC.',
    source: 'Retailer listings for CHDHX-131-RW',
  },
  {
    brandId: 'dji',
    name: 'DJI',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'DJI: CP.MA.00000nnn (cameras/drones) — same dotted family as Hasselblad CP.HB.',
    skuExample: 'CP.MA.00000838',
    mpnRule: 'CP.MA / CP.OS / CP.PT family. Marketing name (Mini 5 Pro) is not the SKU.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: EAN_AS_UPC_TEXT,
    eanLooksLike: 'Examples 6941565996091 / 6941565969040 (GS1 CN). Store EAN-13 in upc.',
    gs1Notes: 'GS1 CN 694. No separate US UPC-A found.',
    source: 'EU distributor listings for Mini 4/5 Pro',
  },
  {
    brandId: 'insta360',
    name: 'Insta360',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Insta360: CINS + letters, e.g. CINSABMA (X4), CINSABTA (Luna), CINSABNB (Link 2).',
    skuExample: 'CINSABMA',
    mpnRule: 'CINS… code. Color suffix like CINSABQB-G is a different simple.',
    barcodeModel: 'independent-regional',
    upcLooksLike:
      'US retail often has a 12-digit UPC (842126…). Do not overwrite it with the China EAN.',
    eanLooksLike:
      'China/EU EAN-13 697… is a different GS1 number (e.g. X4 6970357855506 vs US UPC 842126…). Same pattern as Nikon.',
    gs1Notes: 'US 842126 and GS1 CN 697 on the same MPN — treat as independent-regional, not EAN-as-UPC.',
    source: 'CMS / Best Buy / IPEX listings',
  },
  {
    brandId: 'superior-seamless',
    aliases: ['Superior', 'Superior Specialties'],
    name: 'Superior Seamless',
    carried: true,
    confidence: 'motzu',
    skuLooksLike:
      'Superior Seamless: the colour number on superiorseamlesspaper.com (#93 Arctic White, #57 Thunder Gray) plus the roll size, one product per colour and size (93-53x36, 93-107x36). Superior\'s own per-size part numbers (dealer codes like 101263C) are not printed on the site.',
    skuExample: '93-53x36',
    mpnRule: 'Same colour-and-size code. The site lists one colour number per product; the size suffix is the published roll size.',
    barcodeModel: 'upc-a',
    upcLooksLike:
      'The site publishes no barcode. Superior is US-made, so expect a 12-digit UPC-A per colour and size; it has to come from a dealer or distributor feed.',
    scraperHint:
      'superiorseamlesspaper.com (official, Videndum Media Solutions): WooCommerce Store API for the 54 colours, product page for roll sizes and weights. No prices and no barcodes are published. Colour #41 Marine Blue carries Store API sku 49-1; the scraper uses the number in the title.',
    source: 'Live superiorseamlesspaper.com Store API + product pages, 2026-10-05',
  },
  {
    brandId: 'godox',
    name: 'Godox',
    carried: true,
    confidence: 'researched',
    skuLooksLike:
      'Godox: the model as godox.us prints it, uppercase with spaces dropped (IT32, AD100PROII, V1PRON). Mount suffix is part of the code (V1PRON vs V1PROC). Kits add a suffix (LP600BIK3 = 3-light kit); some accessories use a dashed part number (CBA-WB0002, 910-RFT00-001).',
    skuExample: 'AD100PROII',
    mpnRule: 'Same model string. Mount suffix (V1N vs V1C) is a different simple.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike:
      'Godox items carry EAN-13 6952344… (GS1 CN), stored in upc and ean. KNOWLED items carry a US UPC-A 810216…, stored in upc only.',
    eanLooksLike: 'Example 6952344211007 (GS1 CN). Store EAN-13 in upc.',
    scraperHint: 'godox.us (Godox USA, BigCommerce): BCData sku/upc on every product page. A 1.0 lb weight with no kg row is the store default, not a measurement.',
    source: 'Live godox.us catalog (BCData sku + upc), 2026-10-02; GS1 lookup 6952344211007',
  },
  {
    brandId: 'laowa',
    aliases: ['venus optics', 'venusoptics'],
    name: 'Laowa',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Laowa / Venus: long mount-encoded code (LWA9RLFF56NIKZ) or the printed model+mount.',
    skuExample: 'LWA9RLFF56NIKZ',
    mpnRule: 'Mount-specific. Same focal length on RF vs Z is two simples.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: EAN_AS_UPC_TEXT,
    eanLooksLike: 'Listings use 69404867… (GS1 CN). Store EAN-13 in upc.',
    source: 'EU retailer EAN 6940486701159',
  },
  {
    brandId: 'manfrotto',
    name: 'Manfrotto',
    carried: true,
    confidence: 'researched',
    skuLooksLike:
      'Manfrotto: the code manfrotto.com prints under the title (405, MVH502AH, MT055CXPRO4). Bags keep the "MB " prefix with its space (MB MA3-BP-GM). Short numeric codes (013, 405) are real Manfrotto codes, not retailer SKUs.',
    skuExample: 'MVH502AH',
    mpnRule: 'Same model code.',
    barcodeModel: 'independent-regional',
    upcLooksLike: 'US/Bogen-era UPC-A 719821… is real. Do not replace it with the Italian EAN.',
    eanLooksLike: 'Vitec EU EAN-13 802422… is a different GS1 number than 719821…',
    gs1Notes: 'Same dual-barcode pattern as Gitzo / Lowepro.',
    scraperHint:
      'manfrotto.com (global-en) sells nothing direct: content from the product page, US UPC from the Manfrotto PriceSpider widget (its dealer prices are retail street, never used; no manufacturer price is published). "LL " codes on the same site are Lastolite / Colorama, and Avenger items are listed there too; neither is Manfrotto.',
    source: 'Live manfrotto.com + Manfrotto PriceSpider US widget (UPC 719821193500 for 405), 2026-10-02; upcitemdb 719821276890; Vitec EANs 802422…',
  },
  {
    brandId: 'gitzo',
    name: 'Gitzo',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Gitzo: series model GT3543LS / GH3382QD — that string is the catalog SKU.',
    skuExample: 'GT3543LS',
    mpnRule: 'Same model code.',
    barcodeModel: 'independent-regional',
    upcLooksLike: 'May have a US 719821… UPC and a separate EU 802422… EAN.',
    eanLooksLike: 'Example GT3543LS EAN 8024221640465.',
    source: 'Gitzo EU shop EAN + Vitec group',
  },
  {
    brandId: 'lowepro',
    name: 'Lowepro',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Lowepro: LP + 5 digits + color, e.g. LP37176-GRL.',
    skuExample: 'LP37176-GRL',
    mpnRule: 'Same LP code.',
    barcodeModel: 'independent-regional',
    upcLooksLike: 'US UPC 719821478157 and EU EAN 8024221725124 are both printed for LP37176-GRL.',
    eanLooksLike: INDEPENDENT_EAN_TEXT,
    source: 'Photopoint / GS1 listing for ProTactic 350 AW II',
  },
  {
    brandId: 'profoto',
    name: 'Profoto',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Profoto: 6-digit product number, sometimes with region (901081, 901076-US).',
    skuExample: '901081',
    mpnRule: 'Product number. -US / -EU kit suffixes are different simples.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: 'Swedish EAN-13 734… (example Pro-B3 7340027566015). Store EAN-13 in upc.',
    source: 'Profoto site + Studio Depot listing',
  },
  {
    brandId: 'sandisk',
    name: 'SanDisk',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'SanDisk: capacity-encoded model SDSDXDM-128G-GN4IN (form + size + region).',
    skuExample: 'SDSDXDM-128G-GN4IN',
    mpnRule: 'Full model number. 64G vs 128G are different simples.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'sandisk.com Extreme PRO V90 spec',
  },
  {
    brandId: 'blackmagic',
    aliases: ['blackmagic design'],
    name: 'Blackmagic Design',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Blackmagic: long mnemonic MPN CINECAMPOCHDEF6K — not the retailer BL6KPKCAM.',
    skuExample: 'CINECAMPOCHDEF6K',
    mpnRule: 'Official MPN. Software SKUs (DaVinci) have no barcode.',
    barcodeModel: 'unknown',
    upcLooksLike: DEFAULT_UPC,
    source: 'B&H MFR # for Pocket 6K',
  },
  {
    brandId: 'atomos',
    name: 'Atomos',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'Atomos: ATOM + product, e.g. ATOMNJVPL2, ATOMSHGU01.',
    skuExample: 'ATOMNJVPL2',
    mpnRule: 'Same ATOM… code.',
    barcodeModel: 'upc-a',
    upcLooksLike: 'US UPC-A exists on some kits (example 814164022606).',
    source: 'Retailer listings',
  },
  {
    brandId: 'red',
    aliases: ['red digital cinema'],
    name: 'RED',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'RED: hyphenated numeric MFR 710-0333 (KOMODO). Retailer REKOMB is not the MPN.',
    skuExample: '710-0333',
    mpnRule: 'Numeric 710-… family.',
    barcodeModel: 'unknown',
    upcLooksLike: DEFAULT_UPC,
    source: 'B&H MFR # for KOMODO 6K',
  },
  {
    brandId: 'arri',
    name: 'ARRI',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'ARRI: K0. / K2. order numbers plus marketing name. Confirm on the official price list.',
    mpnRule: 'Order number, not the marketing nickname.',
    barcodeModel: 'unknown',
    upcLooksLike: 'Cinema products often ship with EAN-13 or no retail barcode.',
    source: 'Industry listing pattern — not Motzu-verified',
  },
  {
    brandId: 'zeiss',
    name: 'Zeiss',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Zeiss: dashed order number (2231-636 style) plus lens name (Otus / Batis / ZM).',
    mpnRule: 'Order number is the identity; mount variants differ.',
    barcodeModel: 'unknown',
    upcLooksLike: 'Often EAN-13 or JAN, not a US UPC-A.',
    source: 'Industry listing pattern — not Motzu-verified',
  },
  {
    brandId: 'voigtlander',
    aliases: ['voigtländer'],
    name: 'Voigtlander',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Voigtlander: Cosina model name + mount (Nokton 50/1.5 II M). Confirm numeric order code on the box.',
    mpnRule: 'Mount + finish (black/silver, SC/MC) are different simples.',
    barcodeModel: 'unknown',
    upcLooksLike: 'Typically EAN-13 from Cosina / German importer.',
    source: 'voigtlaender.de lens lines',
  },
  {
    brandId: 'b_w',
    aliases: ['B+W', 'B&W', 'bw', 'b-w', 'B+W Filter', 'B + W', 'Schneider B+W'],
    name: 'B+W',
    carried: false,
    confidence: 'researched',
    skuLooksLike: 'B+W: filter series + diameter (e.g. 010 UV-Haze 52mm). Confirm the boxed Schneider / B+W code.',
    mpnRule: 'Diameter and coating (MRC / XS-Pro) are different simples.',
    barcodeModel: 'unknown',
    upcLooksLike: 'German/EU EAN-13 is typical on B+W / Schneider Kreuznach boxes; confirm before treating as UPC-A.',
    source: 'bw-filters.com / Schneider B+W retail listings',
  },
  {
    brandId: 'hoya',
    name: 'Hoya',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Hoya: filter series + diameter. Confirm the boxed SKU; aftermarket kits share UPCs.',
    mpnRule: 'Diameter is part of the identity.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'US retail UPC listings (mixed GS1 owners — verify)',
  },
  {
    brandId: 'ilford',
    name: 'Ilford',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Ilford: numeric catalog per stock+format+length (HP5+ 36exp ≠ 120).',
    mpnRule: 'Format and exposure count are different simples.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: 'UK/EU EAN-13 is typical; US boxes may also carry a UPC-A.',
    source: 'Industry film catalog pattern',
  },
  {
    brandId: 'kodak',
    name: 'Kodak',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Kodak: CAT / 7-digit catalog (e.g. Tri-X 400 135-36) — not the marketing name alone.',
    mpnRule: 'CAT number + format.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'US boxed film convention',
  },
  {
    brandId: 'kodak_pixpro',
    name: 'Kodak PIXPRO',
    carried: true,
    confidence: 'researched',
    skuLooksLike:
      'Kodak PIXPRO (JK Imaging): the model code for cameras (FZ45, AZ528, WPZ2) and a part number for accessories (LB-060, MT-HD-BK01). The shop sells one code per camera with a colour option; retailers list colour simples with a suffix (FZ45BK / AZ528-BK).',
    skuExample: 'AZ528',
    mpnRule: 'Model or part number as printed. Colour (BK/WH/RD) is a different simple at retail.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    scraperHint:
      'kodakpixpro.com (OpenCart /shop/ + /cameras/<line>/<model>/ pages): product code, price, MSRP, spec table and weight; no UPC/EAN anywhere, so UPC comes from enrichment.',
    source: 'Live kodakpixpro.com shop and camera pages, 2026-10-02',
  },
  {
    brandId: 'broncolor',
    name: 'Broncolor',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Broncolor: numeric article codes on EU price lists.',
    mpnRule: 'Article number.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: 'Swiss/EU EAN-13 expected.',
    source: 'EU lighting house pattern',
  },
  {
    brandId: 'elinchrom',
    name: 'Elinchrom',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Elinchrom: numeric article codes (often 4–5 digits).',
    mpnRule: 'Article number.',
    barcodeModel: 'ean-as-upc',
    upcLooksLike: 'Swiss/EU EAN-13 expected.',
    source: 'EU lighting house pattern',
  },
  {
    brandId: 'really_right_stuff',
    aliases: ['rrs', 'really right stuff'],
    name: 'Really Right Stuff',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'RRS: short alphanumeric plate/clamp codes (B1X, LCF-14).',
    mpnRule: 'Same code.',
    barcodeModel: 'upc-a',
    upcLooksLike: DEFAULT_UPC,
    source: 'US accessory house pattern',
  },
  {
    brandId: 'arca_swiss',
    aliases: ['arca-swiss'],
    name: 'Arca-Swiss',
    carried: false,
    confidence: 'inferred',
    skuLooksLike: 'Arca-Swiss: numeric / hyphenated head codes. Confirm on the official list.',
    mpnRule: 'Official article number.',
    barcodeModel: 'unknown',
    upcLooksLike: 'EU EAN-13 likely.',
    source: 'EU support house pattern',
  },
];

const BY_KEY = new Map<string, BrandCatalogIdentity>();
for (const row of BRAND_CATALOG_IDENTITY) {
  const remember = (token: string | undefined) => {
    const raw = String(token || '').trim().toLowerCase();
    if (!raw) return;
    BY_KEY.set(raw, row);
    BY_KEY.set(raw.replace(/[\s-]+/g, '_'), row);
    BY_KEY.set(raw.replace(/[^a-z0-9]+/g, ''), row);
  };
  remember(row.brandId);
  remember(row.name);
  for (const alias of row.aliases || []) remember(alias);
}

export function normalizeBrandKey(brandId?: string | null, brand?: string | null): string {
  return String(brandId || brand || '')
    .trim()
    .toLowerCase();
}

export function brandCatalogIdentity(
  brandId?: string | null,
  brand?: string | null,
): BrandCatalogIdentity | undefined {
  const key = normalizeBrandKey(brandId, brand);
  if (!key) return undefined;
  const compact = key.replace(/[^a-z0-9]+/g, '');
  return (
    BY_KEY.get(key) ||
    BY_KEY.get(key.replace(/[\s-]+/g, '_')) ||
    BY_KEY.get(key.replace(/_/g, ' ')) ||
    (compact ? BY_KEY.get(compact) : undefined)
  );
}

/** Trademark / catalog display name for a known brandId or alias. */
export function officialBrandDisplayName(
  brandId?: string | null,
  brand?: string | null,
): string | undefined {
  return (
    brandCatalogIdentity(brandId, brand)?.name ||
    brandCatalogIdentity(undefined, brand)?.name ||
    brandCatalogIdentity(brandId)?.name
  );
}

/**
 * Collapse ProMaster / Promaster / SMALLRIG / nisi onto the catalog spelling.
 * Unknown brands keep the incoming label.
 */
export function guardCatalogBrandName(
  brandId?: string | null,
  brand?: string | null,
): string {
  const official = officialBrandDisplayName(brandId, brand);
  if (official) return official;
  return String(brand || '').trim();
}

export function defaultSkuLooksLike(): string {
  return 'A vendor catalog / stock-keeping code — not a Shopify -1 suffix, URL handle, or barcode.';
}

export function defaultUpcLooksLike(): string {
  return DEFAULT_UPC;
}

export function defaultEanLooksLike(): string {
  return DEFAULT_EAN;
}

export function catalogUsesEanAsUpc(brandId?: string | null, brand?: string | null): boolean {
  return brandCatalogIdentity(brandId, brand)?.barcodeModel === 'ean-as-upc';
}

export function catalogKeepsIndependentRegionalEan(
  brandId?: string | null,
  brand?: string | null,
): boolean {
  return brandCatalogIdentity(brandId, brand)?.barcodeModel === 'independent-regional';
}

/** Brand ids (plus aliases) whose catalog barcodeModel is ean-as-upc. */
export function catalogEanAsUpcBrandIds(): string[] {
  return brandIdsForBarcodeModel('ean-as-upc');
}

/** Brand ids (plus aliases) whose catalog barcodeModel is independent-regional. */
export function catalogIndependentRegionalBrandIds(): string[] {
  return brandIdsForBarcodeModel('independent-regional');
}

function brandIdsForBarcodeModel(model: BarcodeModel): string[] {
  const ids = new Set<string>();
  for (const row of BRAND_CATALOG_IDENTITY) {
    if (row.barcodeModel !== model) continue;
    ids.add(row.brandId);
    for (const alias of row.aliases || []) {
      ids.add(alias.toLowerCase().replace(/[\s-]+/g, '_'));
      ids.add(alias.toLowerCase());
    }
  }
  return Array.from(ids);
}
